type Step = 1 | 2 | 3 | 4;

interface WizardState {
  step: Step;
  slug: string;
  name: string;
  stagingDbUser: string;
  stagingDbPassword: string;
  stagingBasicAuthUser: string;
  stagingBasicAuthPassword: string;
  liveSshHost: string;
  liveSshPort: number;
  liveSshUser: string;
  liveSshPrivateKey: string;
  liveUrl: string;
  liveJoomlaPath: string;
  liveDbHost?: string;
  liveDbName?: string;
  liveDbUser?: string;
  liveDbPassword?: string;
  liveDbPort?: number;
  checks: Partial<Record<
    "slug" | "dns" | "stagingDir" | "stagingDb" | "stagingHttps" | "ssh" | "joomlaPath" | "liveDb",
    boolean
  >>;
}

const DRAFT_KEY = "stage-app:wizard-draft";

function emptyState(): WizardState {
  return {
    step: 1,
    slug: "", name: "",
    stagingDbUser: "", stagingDbPassword: "",
    stagingBasicAuthUser: "preview", stagingBasicAuthPassword: "",
    liveSshHost: "", liveSshPort: 22, liveSshUser: "",
    liveSshPrivateKey: "", liveUrl: "", liveJoomlaPath: "",
    checks: {},
  };
}

function loadDraft(): WizardState {
  try {
    const raw = localStorage.getItem(DRAFT_KEY);
    if (raw) return { ...emptyState(), ...JSON.parse(raw) };
  } catch { /* ignore */ }
  return emptyState();
}

function saveDraft(s: WizardState): void {
  try { localStorage.setItem(DRAFT_KEY, JSON.stringify(s)); } catch { /* ignore */ }
}

function clearDraft(): void {
  try { localStorage.removeItem(DRAFT_KEY); } catch { /* ignore */ }
}

interface ApiHelpers {
  api: <T>(
    path: string,
    method: "GET" | "POST" | "PATCH" | "PUT" | "DELETE",
    body?: unknown,
  ) => Promise<{ status: number; data: T | null }>;
  openModal: (templateId: string) => HTMLElement;
  setMsg: (el: HTMLElement | null, text: string, kind: "success" | "error" | "") => void;
  onSaved: () => Promise<void>;
}

export async function openWizard(helpers: ApiHelpers): Promise<void> {
  const m = helpers.openModal("tpl-wizard-modal");
  const state = loadDraft();
  const msgEl = m.querySelector('[data-msg="wizard"]') as HTMLElement;

  // ---- Element refs ----
  const stepperEls = m.querySelectorAll<HTMLElement>("[data-step]");
  const stepContents = m.querySelectorAll<HTMLElement>("[data-step-content]");
  const prevBtn = m.querySelector('[data-action="prev"]') as HTMLButtonElement;
  const nextBtn = m.querySelector('[data-action="next"]') as HTMLButtonElement;
  const saveBtn = m.querySelector('[data-action="save"]') as HTMLButtonElement;
  const checkStagingBtn = m.querySelector('[data-action="check-staging"]') as HTMLButtonElement;
  const checkSshBtn = m.querySelector('[data-action="check-ssh"]') as HTMLButtonElement;
  const fetchConfigBtn = m.querySelector('[data-action="fetch-config"]') as HTMLButtonElement;
  const checkLiveDbBtn = m.querySelector('[data-action="check-live-db"]') as HTMLButtonElement;
  const reverifyBtn = m.querySelector('[data-action="reverify"]') as HTMLButtonElement;
  const borrowSelect = m.querySelector("[data-borrow-select]") as HTMLSelectElement;
  const configDisplay = m.querySelector("[data-config-display]") as HTMLElement;
  const fetchConfigMsg = m.querySelector('[data-msg="fetch-config"]') as HTMLElement;
  const saveMsg = m.querySelector('[data-msg="save"]') as HTMLElement;

  // Set Plesk IP placeholder text from a no-arg DNS check (returns expected)
  void (async () => {
    const r = await helpers.api<{ expected: string }>("/api/wizard/check-dns", "POST", { hostname: "stage.wipakademie.de" });
    const ip = r.data?.expected ?? "(unbekannt)";
    m.querySelectorAll<HTMLElement>('[data-derived="pleskIp"]').forEach((el) => { el.textContent = ip; });
  })();

  // Populate borrow-from-slug dropdown
  void (async () => {
    const r = await helpers.api<{ projects: Array<{ slug: string; name: string }> }>("/api/projects", "GET");
    const opts = r.data?.projects ?? [];
    for (const p of opts) {
      const o = document.createElement("option");
      o.value = p.slug;
      o.textContent = `${p.name} (${p.slug})`;
      borrowSelect.appendChild(o);
    }
  })();

  // ---- Field <-> state binding ----
  const fieldMap: Record<string, keyof WizardState> = {
    slug: "slug", name: "name",
    stagingDbUser: "stagingDbUser", stagingDbPassword: "stagingDbPassword",
    stagingBasicAuthUser: "stagingBasicAuthUser", stagingBasicAuthPassword: "stagingBasicAuthPassword",
    liveSshHost: "liveSshHost", liveSshUser: "liveSshUser",
    liveSshPrivateKey: "liveSshPrivateKey",
    liveUrl: "liveUrl", liveJoomlaPath: "liveJoomlaPath",
  };
  for (const [name, key] of Object.entries(fieldMap)) {
    const el = m.querySelector(`[name="${name}"]`) as HTMLInputElement | HTMLTextAreaElement | null;
    if (!el) continue;
    el.value = String(state[key] ?? "");
    el.addEventListener("input", () => {
      (state as unknown as Record<string, string>)[key] = el.value;
      updateDerivedDisplays();
      saveDraft(state);
      if (name === "slug") void debouncedSlugCheck();
      else updateNav();
    });
  }
  // Special: liveSshPort (number)
  const portEl = m.querySelector('[name="liveSshPort"]') as HTMLInputElement;
  portEl.value = String(state.liveSshPort);
  portEl.addEventListener("input", () => {
    state.liveSshPort = Number(portEl.value) || 22;
    saveDraft(state);
  });

  // Borrow-key dropdown
  borrowSelect.addEventListener("change", async () => {
    const slug = borrowSelect.value;
    if (!slug) return;
    const r = await helpers.api<{ ok: boolean; privateKey?: string }>("/api/wizard/borrow-ssh-key", "POST", { fromSlug: slug });
    if (r.status === 200 && r.data?.ok && r.data.privateKey) {
      const ta = m.querySelector('[name="liveSshPrivateKey"]') as HTMLTextAreaElement;
      ta.value = r.data.privateKey;
      state.liveSshPrivateKey = r.data.privateKey;
      saveDraft(state);
      helpers.setMsg(msgEl, `Key von ${slug} übernommen.`, "success");
    }
  });

  // ---- Derived-field display (slug → docroot, dbname, etc.) ----
  function updateDerivedDisplays(): void {
    const dnsHost = state.slug ? `staging.${state.slug}.wipakademie.de` : "staging.<slug>.wipakademie.de";
    const docRoot = state.slug ? `/var/www/vhosts/wipakademie.de/staging.${state.slug}.wipakademie.de` : "staging.<slug>.wipakademie.de";
    const dbName = state.slug ? `staging_${state.slug}` : "staging_<slug>";
    m.querySelectorAll<HTMLElement>('[data-derived="dnsHost"]').forEach((el) => { el.textContent = dnsHost; });
    m.querySelectorAll<HTMLElement>('[data-derived="docRoot"]').forEach((el) => { el.textContent = docRoot; });
    m.querySelectorAll<HTMLElement>('[data-derived="dbName"]').forEach((el) => { el.textContent = dbName; });
  }
  updateDerivedDisplays();

  /** Format a server response into a human-readable error string.
   *  Surfaces zod validation `issues` array (which lists the failing fields)
   *  alongside the top-level `error` so the user sees e.g.
   *  "validation_failed — ssh.privateKey: String must contain at least 1 character(s)"
   *  instead of the bare "validation_failed". */
  function formatError(
    data: { error?: string; issues?: Array<{ path?: Array<string | number>; message?: string }> } | null | undefined,
    status: number,
  ): string {
    if (!data) return `HTTP ${status}`;
    let msg = data.error ?? `HTTP ${status}`;
    if (Array.isArray(data.issues) && data.issues.length > 0) {
      const items = data.issues.map((iss) => {
        const path = Array.isArray(iss.path) && iss.path.length > 0 ? iss.path.join(".") : "(unknown)";
        return `${path}: ${iss.message ?? "?"}`;
      });
      msg += " — " + items.join(" • ");
    }
    return msg;
  }

  // ---- Check rendering ----
  function renderCheck(name: string, ok: boolean | undefined, label: string, error?: string): void {
    m.querySelectorAll<HTMLElement>(`[data-check="${name}"]`).forEach((el) => {
      el.classList.remove("ok", "fail");
      if (ok === true) {
        el.classList.add("ok");
        el.textContent = `✅ ${label}`;
      } else if (ok === false) {
        el.classList.add("fail");
        el.textContent = `❌ ${label}${error ? ` — ${error}` : ""}`;
      } else {
        el.textContent = `⏳ ${label}`;
      }
    });
  }

  function renderAllChecksFromState(): void {
    renderCheck("slug", state.checks.slug, "Slug-Verfügbarkeit");
    renderCheck("dns", state.checks.dns, "DNS-A-Record");
    renderCheck("stagingDir", state.checks.stagingDir, "Staging-Verzeichnis");
    renderCheck("stagingDb", state.checks.stagingDb, "MariaDB");
    renderCheck("stagingHttps", state.checks.stagingHttps, "HTTPS");
    renderCheck("ssh", state.checks.ssh, "SSH-Verbindung");
    renderCheck("joomlaPath", state.checks.joomlaPath, "Joomla-Pfad");
    renderCheck("liveDb", state.checks.liveDb, "Live-DB");
  }
  renderAllChecksFromState();

  // Restore action-button enabled states from saved checks. Without this, a
  // user who reloads after passing SSH+joomla-path in a prior session sees the
  // ✅ but can't click "📥 configuration.php holen" because the button's
  // disabled attribute is hardcoded in the HTML and only flipped inside
  // runSshChecks. Same for "🔍 Live-DB testen" after a successful fetch.
  if (state.checks.ssh === true && state.checks.joomlaPath === true) {
    fetchConfigBtn.disabled = false;
  }
  if (state.liveDbHost && state.liveDbName && state.liveDbUser) {
    checkLiveDbBtn.disabled = false;
    // Also re-show the parsed-config card so the user sees what was fetched
    configDisplay.style.display = "";
    const hostDisplay = state.liveDbPort === 3306 ? state.liveDbHost : `${state.liveDbHost} (Port ${state.liveDbPort})`;
    (m.querySelector('[data-config-field="host"]') as HTMLElement).textContent = hostDisplay;
    (m.querySelector('[data-config-field="db"]') as HTMLElement).textContent = state.liveDbName;
    (m.querySelector('[data-config-field="user"]') as HTMLElement).textContent = state.liveDbUser;
    if (state.liveDbPassword) {
      (m.querySelector('[data-config-field="password"]') as HTMLElement).textContent = state.liveDbPassword;
    }
    // dbprefix isn't in WizardState (not part of the saved project payload),
    // so leave it blank on restore — it'll repopulate if user re-fetches.
  }

  // Delegated copy-button handler — clicks on .copy-btn copy the previous
  // <code> sibling's textContent to clipboard and flash ✓ briefly.
  // NOTE: password-reveal (.reveal-btn) is handled in openModal so it works
  // across all modals (wizard, edit-form, change-password) without per-modal
  // wiring. Don't duplicate that here.
  m.addEventListener("click", async (ev) => {
    const target = ev.target as HTMLElement;
    if (!target.classList.contains("copy-btn")) return;
    ev.preventDefault();
    const code = target.previousElementSibling as HTMLElement | null;
    const text = code?.textContent?.trim() ?? "";
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      const btn = target as HTMLButtonElement;
      const orig = btn.textContent;
      btn.textContent = "✓";
      btn.classList.add("copied");
      setTimeout(() => {
        btn.textContent = orig;
        btn.classList.remove("copied");
      }, 1500);
    } catch { /* clipboard API blocked — silently ignore */ }
  });

  // ---- Slug-check (debounced) ----
  let slugTimer: ReturnType<typeof setTimeout> | null = null;
  async function debouncedSlugCheck(): Promise<void> {
    if (slugTimer) clearTimeout(slugTimer);
    slugTimer = setTimeout(async () => {
      if (!state.slug) {
        delete state.checks.slug;
        renderCheck("slug", undefined, "Slug-Verfügbarkeit");
        updateNav();
        return;
      }
      if (!/^[a-z0-9][a-z0-9-]*$/.test(state.slug)) {
        state.checks.slug = false;
        renderCheck("slug", false, "Slug-Verfügbarkeit", "ungültiges Format");
        updateNav();
        return;
      }
      const r = await helpers.api<{ available: boolean }>("/api/wizard/check-slug", "POST", { slug: state.slug });
      state.checks.slug = r.status === 200 && r.data?.available === true;
      renderCheck("slug", state.checks.slug, "Slug-Verfügbarkeit", state.checks.slug ? undefined : "bereits vergeben");
      saveDraft(state);
      updateNav();
    }, 300);
  }

  // ---- Step 2 checks ----
  async function runStagingChecks(): Promise<void> {
    const dnsHost = `staging.${state.slug}.wipakademie.de`;
    const docRoot = `/var/www/vhosts/wipakademie.de/${dnsHost}`;
    const dbName = `staging_${state.slug}`;
    const stagingUrl = `https://${dnsHost}`;

    // Reset to ⏳ so the user sees something happens on click even if all
    // checks were already green from a prior run.
    delete state.checks.dns;
    delete state.checks.stagingDir;
    delete state.checks.stagingDb;
    delete state.checks.stagingHttps;
    renderCheck("dns", undefined, "DNS-A-Record");
    renderCheck("stagingDir", undefined, "Staging-Verzeichnis");
    renderCheck("stagingDb", undefined, "MariaDB");
    renderCheck("stagingHttps", undefined, "HTTPS");
    updateNav();

    const [dns, dir, db, https] = await Promise.all([
      helpers.api<{ ok: boolean; resolvedTo?: string; error?: string }>("/api/wizard/check-dns", "POST", { hostname: dnsHost }),
      helpers.api<{ ok: boolean; error?: string }>("/api/wizard/check-staging-dir", "POST", { path: docRoot }),
      helpers.api<{ ok: boolean; error?: string }>("/api/wizard/check-staging-db", "POST", {
        dbName, dbUser: state.stagingDbUser, dbPass: state.stagingDbPassword,
      }),
      helpers.api<{ ok: boolean; statusCode?: number; error?: string }>("/api/wizard/check-staging-https", "POST", { url: stagingUrl }),
    ]);
    state.checks.dns = dns.data?.ok === true;
    state.checks.stagingDir = dir.data?.ok === true;
    state.checks.stagingDb = db.data?.ok === true;
    state.checks.stagingHttps = https.data?.ok === true;
    renderCheck("dns", state.checks.dns, "DNS-A-Record",
      state.checks.dns ? undefined : (formatError(dns.data, dns.status) || `aktuell: ${dns.data?.resolvedTo ?? "?"}`));
    renderCheck("stagingDir", state.checks.stagingDir, "Staging-Verzeichnis",
      state.checks.stagingDir ? undefined : formatError(dir.data, dir.status));
    renderCheck("stagingDb", state.checks.stagingDb, "MariaDB",
      state.checks.stagingDb ? undefined : formatError(db.data, db.status));
    renderCheck("stagingHttps", state.checks.stagingHttps, "HTTPS",
      state.checks.stagingHttps ? undefined : formatError(https.data, https.status));
    saveDraft(state);
    updateNav();
  }

  // ---- Step 3 checks ----
  async function runSshChecks(): Promise<void> {
    const ssh = {
      host: state.liveSshHost, port: state.liveSshPort,
      user: state.liveSshUser, privateKey: state.liveSshPrivateKey,
    };
    // Reset to ⏳ for visible feedback on retry
    delete state.checks.ssh;
    delete state.checks.joomlaPath;
    renderCheck("ssh", undefined, "SSH-Verbindung");
    renderCheck("joomlaPath", undefined, "Joomla-Pfad");
    updateNav();

    const sshResult = await helpers.api<{ ok: boolean; error?: string }>("/api/wizard/test-ssh-direct", "POST", { ssh });
    state.checks.ssh = sshResult.data?.ok === true;
    renderCheck("ssh", state.checks.ssh, "SSH-Verbindung",
      state.checks.ssh ? undefined : formatError(sshResult.data, sshResult.status));

    if (state.checks.ssh) {
      const pathResult = await helpers.api<{ ok: boolean; exists: boolean }>("/api/wizard/check-live-joomla-path", "POST", { ssh, path: state.liveJoomlaPath });
      state.checks.joomlaPath = pathResult.data?.exists === true;
      renderCheck("joomlaPath", state.checks.joomlaPath, "Joomla-Pfad", state.checks.joomlaPath ? undefined : "nicht gefunden");
      fetchConfigBtn.disabled = !(state.checks.ssh && state.checks.joomlaPath);
    }
    saveDraft(state);
    updateNav();
  }

  async function fetchLiveConfig(): Promise<void> {
    const ssh = {
      host: state.liveSshHost, port: state.liveSshPort,
      user: state.liveSshUser, privateKey: state.liveSshPrivateKey,
    };
    helpers.setMsg(fetchConfigMsg, "lade …", "");
    const r = await helpers.api<{ ok: boolean; parsed?: { host: string; db: string; user: string; password: string; dbprefix: string }; missing?: string[]; error?: string }>(
      "/api/wizard/fetch-live-config", "POST", { ssh, joomlaPath: state.liveJoomlaPath },
    );
    if (r.status === 200 && r.data?.ok && r.data.parsed) {
      const p = r.data.parsed;
      // Joomla configuration.php on IONOS often stores host as "host:port"
      // (e.g. "db5015836499.hosting-data.io:3306") because mysqli accepts that
      // form. The mysql CLI does NOT — it needs --host and --port as separate
      // flags. Split before storing.
      let host = p.host;
      let port = 3306;
      const colonIdx = host.lastIndexOf(":");
      if (colonIdx > 0 && /^\d+$/.test(host.slice(colonIdx + 1))) {
        port = Number(host.slice(colonIdx + 1));
        host = host.slice(0, colonIdx);
      }
      state.liveDbHost = host;
      state.liveDbName = p.db;
      state.liveDbUser = p.user;
      state.liveDbPassword = p.password;
      state.liveDbPort = port;
      saveDraft(state);
      configDisplay.style.display = "";
      (m.querySelector('[data-config-field="host"]') as HTMLElement).textContent = port === 3306 ? host : `${host} (Port ${port})`;
      (m.querySelector('[data-config-field="db"]') as HTMLElement).textContent = p.db;
      (m.querySelector('[data-config-field="user"]') as HTMLElement).textContent = p.user;
      (m.querySelector('[data-config-field="password"]') as HTMLElement).textContent = p.password;
      (m.querySelector('[data-config-field="dbprefix"]') as HTMLElement).textContent = p.dbprefix;
      checkLiveDbBtn.disabled = false;
      helpers.setMsg(fetchConfigMsg, "OK — Felder ausgefüllt", "success");
    } else {
      let detail = formatError(r.data, r.status);
      if (r.data?.missing) {
        detail += ` — fehlende Felder in configuration.php: ${r.data.missing.join(", ")}`;
      }
      helpers.setMsg(fetchConfigMsg, "Fehler: " + detail, "error");
    }
  }

  async function runLiveDbCheck(): Promise<void> {
    if (!state.liveDbHost || !state.liveDbName || !state.liveDbUser || !state.liveDbPassword) {
      renderCheck("liveDb", false, "Live-DB", "configuration.php noch nicht geholt");
      return;
    }
    // Reset to ⏳ for visible feedback on retry
    delete state.checks.liveDb;
    renderCheck("liveDb", undefined, "Live-DB");
    updateNav();

    const ssh = {
      host: state.liveSshHost, port: state.liveSshPort,
      user: state.liveSshUser, privateKey: state.liveSshPrivateKey,
    };
    const r = await helpers.api<{ ok: boolean; error?: string }>("/api/wizard/test-live-db-direct", "POST", {
      ssh,
      db: { host: state.liveDbHost, port: state.liveDbPort ?? 3306, user: state.liveDbUser, password: state.liveDbPassword, database: state.liveDbName },
    });
    state.checks.liveDb = r.data?.ok === true;
    renderCheck("liveDb", state.checks.liveDb, "Live-DB",
      state.checks.liveDb ? undefined : formatError(r.data, r.status));
    saveDraft(state);
    updateNav();
  }

  // ---- Wire up action buttons ----
  checkStagingBtn.addEventListener("click", () => { void runStagingChecks(); });
  checkSshBtn.addEventListener("click", () => { void runSshChecks(); });
  fetchConfigBtn.addEventListener("click", () => { void fetchLiveConfig(); });
  checkLiveDbBtn.addEventListener("click", () => { void runLiveDbCheck(); });
  reverifyBtn.addEventListener("click", async () => {
    await runStagingChecks();
    await runSshChecks();
    await runLiveDbCheck();
  });

  // ---- Step navigation ----
  function step1Done(): boolean {
    return state.checks.slug === true && state.name.length > 0;
  }
  function step2Done(): boolean {
    return state.checks.dns === true && state.checks.stagingDir === true
      && state.checks.stagingDb === true && state.checks.stagingHttps === true;
  }
  function step3Done(): boolean {
    return state.checks.ssh === true && state.checks.joomlaPath === true && state.checks.liveDb === true;
  }
  function canSave(): boolean { return step1Done() && step2Done() && step3Done(); }

  function showStep(n: Step): void {
    state.step = n;
    saveDraft(state);
    stepperEls.forEach((el) => {
      const num = Number(el.dataset["step"]);
      el.classList.toggle("active", num === n);
      el.classList.toggle("done", num < n);
    });
    stepContents.forEach((el) => {
      const num = Number(el.dataset["stepContent"]);
      el.classList.toggle("hidden", num !== n);
    });
    updateNav();
  }

  function updateNav(): void {
    prevBtn.disabled = state.step === 1;
    if (state.step === 1) {
      nextBtn.disabled = !step1Done();
      nextBtn.style.display = "";
      saveBtn.style.display = "none";
    } else if (state.step === 2) {
      nextBtn.disabled = !step2Done();
      nextBtn.style.display = "";
      saveBtn.style.display = "none";
    } else if (state.step === 3) {
      nextBtn.disabled = !step3Done();
      nextBtn.style.display = "";
      saveBtn.style.display = "none";
    } else {
      nextBtn.style.display = "none";
      saveBtn.style.display = "";
      saveBtn.disabled = !canSave();
    }
  }

  prevBtn.addEventListener("click", () => { if (state.step > 1) showStep((state.step - 1) as Step); });
  nextBtn.addEventListener("click", () => { if (state.step < 4) showStep((state.step + 1) as Step); });

  saveBtn.addEventListener("click", async () => {
    if (!canSave()) return;
    saveBtn.disabled = true;
    helpers.setMsg(saveMsg, "Speichern …", "");

    const dnsHost = `staging.${state.slug}.wipakademie.de`;
    const payload = {
      slug: state.slug,
      name: state.name,
      stagingRootPath: `/var/www/vhosts/wipakademie.de/${dnsHost}`,
      stagingUrl: `https://${dnsHost}`,
      stagingDbName: `staging_${state.slug}`,
      stagingDbUser: state.stagingDbUser,
      stagingDbPassword: state.stagingDbPassword,
      stagingBasicAuthUser: state.stagingBasicAuthUser,
      stagingBasicAuthPassword: state.stagingBasicAuthPassword,
      liveUrl: state.liveUrl,
      liveSshHost: state.liveSshHost,
      liveSshPort: state.liveSshPort,
      liveSshUser: state.liveSshUser,
      liveSshPrivateKey: state.liveSshPrivateKey,
      liveJoomlaPath: state.liveJoomlaPath,
      liveDbHost: state.liveDbHost,
      liveDbPort: state.liveDbPort ?? 3306,
      liveDbUser: state.liveDbUser,
      liveDbPassword: state.liveDbPassword,
      liveDbName: state.liveDbName,
      anthropicApiKey: null,
      snapshotRetention: 20,
    };
    const r = await helpers.api<{ id?: number; error?: string; issues?: Array<{ path?: Array<string|number>; message?: string }> }>("/api/projects", "POST", payload);
    if (r.status === 200 || r.status === 201) {
      clearDraft();
      const autoPullEl = m.querySelector('[name="autoFirstPull"]') as HTMLInputElement;
      if (autoPullEl?.checked) {
        helpers.setMsg(saveMsg, "Gespeichert. Erste Pull läuft …", "success");
        // Fire-and-forget; the pull endpoint is fire-and-forget via socket.io for progress.
        void helpers.api(`/api/projects/${state.slug}/pull`, "POST", {});
      } else {
        helpers.setMsg(saveMsg, "Gespeichert.", "success");
      }
      await helpers.onSaved();
      setTimeout(() => m.remove(), 800);
    } else {
      let msg = "Fehler: " + (r.data?.error ?? String(r.status));
      if (Array.isArray(r.data?.issues)) {
        msg += " — " + r.data.issues.map((i) => `${i.path?.join(".") ?? "?"}: ${i.message ?? "?"}`).join(" • ");
      }
      helpers.setMsg(saveMsg, msg, "error");
      saveBtn.disabled = false;
    }
  });

  // Restore the step the user was on
  showStep(state.step);
}
